import { useState } from "react";
import { Video } from "lucide-react";

import { ApiError } from "../../lib/api";
import { mapVideoError } from "../../lib/videoDrill";
import { useDispatchVideo } from "../../hooks/useVideo";
import { FormField, inputStyle } from "../../components/ui/FormField";
import { Sheet } from "../../components/Sheet";

interface Props {
  open: boolean;
  engagementId: string;
  engagementName: string;
  onClose: () => void;
}

/**
 * Two-step video dispatch modal.
 *   1. Operator pastes the meeting URL (Zoom / Teams / Meet)
 *   2. Backend returns bot_id; show mission-brief confirmation
 *
 * Mirrors DispatchCallModal's visual language: amber accent line, mono
 * eyebrow, Boardroom font tokens, same button set.
 */
export function VideoDispatchModal({
  open,
  engagementId,
  engagementName,
  onClose,
}: Props) {
  const [meetingUrl, setMeetingUrl] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [botId, setBotId] = useState<string | null>(null);

  const dispatch = useDispatchVideo(engagementId);

  function reset() {
    setMeetingUrl("");
    setError(null);
    setBotId(null);
    dispatch.reset();
  }

  function handleClose() {
    reset();
    onClose();
  }

  async function confirm() {
    const url = meetingUrl.trim();
    if (!url) {
      setError("Paste the meeting URL first.");
      return;
    }
    setError(null);
    try {
      const r = await dispatch.mutateAsync(url);
      setBotId(r.bot_id);
    } catch (e) {
      if (e instanceof ApiError) {
        setError(mapVideoError(e.detail));
      } else {
        setError("Dispatch failed — unknown error");
      }
    }
  }

  return (
    <Sheet open={open} onClose={handleClose}>
      <div style={{ position: "relative", fontFamily: "var(--font-sans)" }}>
        {/* top accent line — amber for dispatch */}
        <span
          aria-hidden
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 1,
            background:
              "linear-gradient(90deg, transparent, var(--cta), transparent)",
          }}
        />

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 16,
          }}
        >
          <div>
            <div
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 10,
                letterSpacing: "0.20em",
                color: "var(--cta)",
                marginBottom: 6,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <Video size={11} strokeWidth={2} />
              {botId ? "DISPATCH ACKNOWLEDGED" : "DISPATCH DRILL"}
            </div>
            <h3
              style={{
                margin: 0,
                fontFamily: "var(--font-display)",
                fontSize: 22,
                fontWeight: 600,
                letterSpacing: "-0.01em",
                color: "var(--text-primary)",
                lineHeight: 1.2,
              }}
            >
              {engagementName}
            </h3>
          </div>
        </div>

        {botId === null ? (
          // ── Step 1: enter meeting URL ─────────────────────────────────
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <FormField label="Meeting URL · Zoom / Teams / Meet">
              <input
                style={inputStyle}
                value={meetingUrl}
                onChange={(e) => setMeetingUrl(e.target.value)}
                placeholder="https://zoom.us/j/…"
              />
            </FormField>

            {error ? <ErrorRow message={error} /> : null}

            <p
              style={{
                margin: 0,
                fontSize: 11.5,
                color: "var(--text-muted)",
                lineHeight: 1.6,
                letterSpacing: "-0.005em",
              }}
            >
              Drops a bot into the meeting that plays the approved clip as its
              camera, then leaves. Watch its status on the engagement page.
            </p>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
                paddingTop: 12,
                borderTop: "1px solid var(--border-subtle)",
              }}
            >
              <button onClick={handleClose} style={ghostButtonStyle}>
                CANCEL
              </button>
              <button
                onClick={() => void confirm()}
                disabled={!meetingUrl.trim() || dispatch.isPending}
                style={
                  !meetingUrl.trim() || dispatch.isPending
                    ? primaryButtonDisabled
                    : primaryButtonEnabled
                }
              >
                {dispatch.isPending ? "DISPATCHING…" : "DISPATCH"}
              </button>
            </div>
          </div>
        ) : (
          // ── Step 2: success / handoff ─────────────────────────────────
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* mission brief — handoff details */}
            <div
              style={{
                background: "var(--bg-tertiary)",
                border: "1px solid var(--border-subtle)",
                borderLeft: "2px solid var(--cta)",
                borderRadius: "var(--radius-input)",
                padding: "14px 16px",
                display: "grid",
                gridTemplateColumns: "120px 1fr",
                gap: "10px 14px",
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                letterSpacing: "0.02em",
                alignItems: "baseline",
              }}
            >
              <Label>BOT ID</Label>
              <Value mono>{botId}</Value>
            </div>

            <p
              style={{
                margin: 0,
                fontSize: 11.5,
                color: "var(--text-muted)",
                lineHeight: 1.6,
              }}
            >
              The bot is joining the meeting now and will play the clip as its
              camera. Track its status on the engagement page.
            </p>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
                paddingTop: 12,
                borderTop: "1px solid var(--border-subtle)",
              }}
            >
              <button onClick={handleClose} style={primaryButtonEnabled}>
                CLOSE
              </button>
            </div>
          </div>
        )}
      </div>
    </Sheet>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: 9.5,
        letterSpacing: "0.18em",
        color: "var(--text-muted)",
      }}
    >
      {children}
    </span>
  );
}

function Value({ children, mono }: { children: React.ReactNode; mono?: boolean }) {
  return (
    <span
      style={{
        color: "var(--text-primary)",
        fontFamily: mono ? "var(--font-mono)" : "var(--font-sans)",
        fontSize: mono ? 11 : 12,
        wordBreak: "break-all",
      }}
    >
      {children}
    </span>
  );
}

function ErrorRow({ message }: { message: string }) {
  return (
    <div
      role="alert"
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: 11,
        letterSpacing: "0.04em",
        color: "var(--error)",
        background: "rgba(244,63,94,0.07)",
        border: "1px solid var(--error)",
        borderRadius: "var(--radius-input)",
        padding: "9px 12px",
      }}
    >
      {message}
    </div>
  );
}

const ghostButtonStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "10px 14px",
  minHeight: "var(--tap-min)",
  borderRadius: "var(--radius-input)",
  border: "1px solid var(--border)",
  background: "transparent",
  color: "var(--text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-mono)",
  fontSize: 10.5,
  letterSpacing: "0.14em",
  fontWeight: 500,
};

const primaryButtonEnabled: React.CSSProperties = {
  padding: "10px 18px",
  minHeight: "var(--tap-min)",
  borderRadius: "var(--radius-input)",
  background: "var(--cta)",
  color: "#1A1108",
  border: "1px solid var(--cta)",
  cursor: "pointer",
  fontFamily: "var(--font-mono)",
  fontSize: 10.5,
  letterSpacing: "0.16em",
  fontWeight: 600,
  boxShadow: "var(--glow-cta)",
};

const primaryButtonDisabled: React.CSSProperties = {
  padding: "10px 18px",
  minHeight: "var(--tap-min)",
  borderRadius: "var(--radius-input)",
  background: "var(--bg-tertiary)",
  color: "var(--text-muted)",
  border: "1px solid var(--border-subtle)",
  cursor: "not-allowed",
  fontFamily: "var(--font-mono)",
  fontSize: 10.5,
  letterSpacing: "0.16em",
  fontWeight: 500,
};
